import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute, NavigationEnd } from '@angular/router';
import { filter, takeUntil, skip, distinctUntilChanged } from 'rxjs/operators';
import { Subject } from 'rxjs';
import {
  ReactiveFormsModule,
  FormsModule,
  FormGroup,
  FormBuilder,
  Validators,
} from '@angular/forms';
import { lastValueFrom } from 'rxjs';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { SelectModule } from 'primeng/select';
import { DatePickerModule } from 'primeng/datepicker';
import { MastersService } from '../../services/masters/masters.service';
import { SubsidiaryService } from '../../services/subsidiary/subsidiary.service';
import { ToasterService, AuthService } from '@ui-nuvertos/auth';
import {
  DynamicListingComponent,
  ColumnConfig,
  ActionConfig,
  ActionEvent,
} from '../../dynamic-listing/dynamic-listing';
import { SVG_ICONS } from '../../utils/svg-icons.utils';

@Component({
  selector: 'lib-fiscal-period',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    ButtonModule,
    InputTextModule,
    InputNumberModule,
    SelectModule,
    DatePickerModule,
    DynamicListingComponent,
  ],
  templateUrl: './fiscal-period.html',
  styleUrl: './fiscal-period.scss',
})
export class FiscalPeriod implements OnInit, OnDestroy {
  masterType = 'fiscal_period';
  isLoading = false;
  tableData: any[] = [];
  private destroy$ = new Subject<void>();

  pageIndex = 1;
  rowsPerPage = 10;
  totalCount = 0;
  totalPages = 0;
  viewMode: 'list' | 'form' = 'list';
  isEditMode = false;
  selectedItemId: string | number | null = null;
  formData: any = null;
  formGroup: FormGroup;

  // Status update fields
  newARStatus: string | null = null;
  newAPStatus: string | null = null;
  newJVStatus: string | null = null;

  // Dropdown options
  subsidiaryOptions: any[] = [];
  selectedListSubsidiaryId: string | number | null = null;

  // Super Admin check - only super admins can create fiscal periods
  isSuperAdmin: boolean = false;
  periodTypeOptions = [
    { label: 'Month', value: 'month' },
    { label: 'Quarter', value: 'quarter' },
    { label: 'Year', value: 'year' },
  ];

  columnConfigs: ColumnConfig[] = [
    { key: 'period_name', header: 'Period Name', type: 'text' },
    { key: 'subsidiary_name', header: 'Subsidiary', type: 'text' },
    { key: 'start_date', header: 'Start Date', type: 'date' },
    { key: 'end_date', header: 'End Date', type: 'date' },
    { key: 'fiscal_year', header: 'Fiscal Year', type: 'text' },
    { key: 'ar_status_display', header: 'AR Status', type: 'text' },
    { key: 'ap_status_display', header: 'AP Status', type: 'text' },
    { key: 'jv_status_display', header: 'JV Status', type: 'text' },
  ];

  actionConfigs: ActionConfig[] = [
    {
      key: 'edit',
      label: 'Edit',
      tooltip: 'Edit / Manage Status',
      severity: 'secondary',
      svgIcon: SVG_ICONS.EDIT,
    },
  ];

  constructor(
    private mastersService: MastersService,
    private subsidiaryService: SubsidiaryService,
    private toasterService: ToasterService,
    private router: Router,
    private route: ActivatedRoute,
    private fb: FormBuilder,
    private authService: AuthService
  ) {
    this.formGroup = this.fb.group({
      subsidiary_id: [null, Validators.required],
      period_name: ['', Validators.required],
      start_date: [null, Validators.required],
      end_date: [null, Validators.required],
      fiscal_year: [new Date().getFullYear(), Validators.required],
      period_type: ['month', Validators.required],
    });
  }

  async ngOnInit() {
    // Check if current user is Super Admin
    this.checkSuperAdminStatus();
    
    await this.loadSubsidiaries();
    await this.checkRouteAndLoad();

    let previousUrl = this.router.url;

    this.route.params
      .pipe(
        skip(1),
        distinctUntilChanged(
          (prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)
        ),
        takeUntil(this.destroy$)
      )
      .subscribe(async () => {
        await this.checkRouteAndLoad();
      });

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(async (event: any) => {
        const currentUrl = event.urlAfterRedirects || event.url;
        if (currentUrl !== previousUrl) {
          previousUrl = currentUrl;
          await this.checkRouteAndLoad();
        }
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Check if the current user has Super Admin role
   */
  private checkSuperAdminStatus(): void {
    const currentUser = this.authService.getCurrentUser();
    this.isSuperAdmin = currentUser?.role === 'Super Admin';
  }

  /**
   * Navigate to create fiscal period page (only for Super Admin)
   */
  onCreateClick(): void {
    if (!this.isSuperAdmin) {
      this.toasterService.error('Access Denied', 'Only Super Admin can create fiscal periods');
      return;
    }
    this.router.navigate(['/settings/masters/fiscal-period/create']);
  }

  async loadSubsidiaries() {
    try {
      const response: any = await lastValueFrom(
        this.subsidiaryService.getSubsidiaries()
      );
      const subsidiaries = response?.data?.subsidiaries || [];
      this.subsidiaryOptions = subsidiaries.map((s: any) => ({
        label: s.subsidiary_name || s.name,
        value: s.id,
      }));

      // Auto-select first subsidiary for list view filter
      if (this.subsidiaryOptions.length > 0 && !this.selectedListSubsidiaryId) {
        this.selectedListSubsidiaryId = this.subsidiaryOptions[0].value;
      }
    } catch (error) {
      console.error('Error loading subsidiaries:', error);
      this.toasterService.error('Error', 'Failed to load subsidiaries');
    }
  }

  async loadFiscalPeriods(page: number = 1) {
    if (!this.selectedListSubsidiaryId) {
      this.tableData = [];
      return;
    }

    try {
      this.isLoading = true;
      const response: any = await lastValueFrom(
        this.mastersService.getFiscalPeriods(this.selectedListSubsidiaryId, undefined, {
          limit: this.rowsPerPage,
          offset: (page - 1) * this.rowsPerPage
        })
      );

      const fiscalPeriods = response?.data?.fiscal_periods || [];

      // Enrich with subsidiary name and formatted status
      this.tableData = fiscalPeriods.map((fp: any) => {
        const subsidiary = this.subsidiaryOptions.find(
          (s) => s.value === fp.subsidiary_id
        );
        return {
          ...fp,
          subsidiary_name: subsidiary?.label || '-',
          ar_status_display: this.formatStatusDisplay(fp.ar_status),
          ap_status_display: this.formatStatusDisplay(fp.ap_status),
          jv_status_display: this.formatStatusDisplay(fp.jv_status),
        };
      });

      this.totalCount = response?.data?.total || this.tableData.length;
      this.totalPages = Math.ceil(this.totalCount / this.rowsPerPage);
      this.pageIndex = page;
    } catch (error) {
      console.error('Error loading fiscal periods:', error);
      this.toasterService.error('Error', 'Failed to load fiscal periods');
    } finally {
      this.isLoading = false;
    }
  }

  async loadItemForEdit(id: string | number) {
    try {
      this.isLoading = true;
      const response: any = await lastValueFrom(
        this.mastersService.getFiscalPeriodById(id)
      );
      this.formData = response?.data || response;
      this.initializeForm();
    } catch (error) {
      console.error('Error loading fiscal period:', error);
      this.toasterService.error('Error', 'Failed to load fiscal period');
    } finally {
      this.isLoading = false;
    }
  }

  initializeForm() {
    if (this.formData) {
      this.formGroup.patchValue({
        subsidiary_id: this.formData.subsidiary_id,
        period_name: this.formData.period_name,
        start_date: this.formData.start_date
          ? new Date(this.formData.start_date)
          : null,
        end_date: this.formData.end_date
          ? new Date(this.formData.end_date)
          : null,
        fiscal_year: this.formData.fiscal_year,
        period_type: this.formData.period_type || 'month',
      });
    } else {
      this.formGroup.reset({
        fiscal_year: new Date().getFullYear(),
        period_type: 'month',
      });
    }

    // Reset status update fields
    this.newARStatus = null;
    this.newAPStatus = null;
    this.newJVStatus = null;
  }

  async checkRouteAndLoad() {
    const url = this.router.url;
    let routeParams = this.route.snapshot.params;
    let childRoute = this.route.snapshot.firstChild;
    while (childRoute) {
      routeParams = { ...routeParams, ...childRoute.params };
      childRoute = childRoute.firstChild;
    }

    let id = routeParams['id'];
    if (!id && url.includes('/edit/')) {
      const match = url.match(/\/edit\/([^\/]+)/);
      if (match) id = match[1];
    }

    if (url.includes('/create') || routeParams['action'] === 'create') {
      this.viewMode = 'form';
      this.isEditMode = false;
      this.selectedItemId = null;
      this.formData = null;
      this.initializeForm();
    } else if (id && url.includes('/edit')) {
      this.selectedItemId = id;
      this.isEditMode = true;
      this.viewMode = 'form';
      await this.loadItemForEdit(id);
    } else {
      this.viewMode = 'list';
      this.isEditMode = false;
      this.selectedItemId = null;
      await this.loadFiscalPeriods();
    }
  }

  onActionClicked(event: ActionEvent) {
    const { action, row } = event;
    if (action === 'edit') {
      this.router.navigate(['/settings/masters/fiscal-period/edit', row.id]);
    }
  }

  onPageChanged(page: number) {
    this.loadFiscalPeriods(page);
  }

  onSubsidiaryChange() {
    this.loadFiscalPeriods(1);
  }

  async onFormSubmit() {
    if (this.formGroup.invalid) {
      this.formGroup.markAllAsTouched();
      return;
    }

    try {
      this.isLoading = true;
      const formValue = this.formGroup.value;

      // Format dates for API
      const payload = {
        ...formValue,
        start_date: this.formatDate(formValue.start_date),
        end_date: this.formatDate(formValue.end_date),
      };

      if (this.isEditMode && this.selectedItemId) {
        await lastValueFrom(
          this.mastersService.updateFiscalPeriod(this.selectedItemId, payload)
        );
        this.toasterService.success(
          'Success',
          'Fiscal period updated successfully'
        );
      } else {
        await lastValueFrom(this.mastersService.createFiscalPeriod(payload));
        this.toasterService.success(
          'Success',
          'Fiscal period created successfully'
        );
      }

      this.router.navigate(['/settings/masters/fiscal-period']);
    } catch (error: any) {
      console.error('Error saving fiscal period:', error);
      const message = error?.error?.message || 'Failed to save fiscal period';
      this.toasterService.error('Error', message);
    } finally {
      this.isLoading = false;
    }
  }

  async onUpdateStatus(statusType: 'ar' | 'ap' | 'jv') {
    const newStatus =
      statusType === 'ar'
        ? this.newARStatus
        : statusType === 'ap'
        ? this.newAPStatus
        : this.newJVStatus;

    if (!newStatus || !this.selectedItemId) return;

    const statusLabel = statusType.toUpperCase();
    const confirmMessage =
      newStatus === 'locked'
        ? `Are you sure you want to LOCK the ${statusLabel} status? This action cannot be undone.`
        : `Are you sure you want to change ${statusLabel} status to "${newStatus}"?`;

    if (!confirm(confirmMessage)) return;

    try {
      this.isLoading = true;
      await lastValueFrom(
        this.mastersService.updateFiscalPeriodStatus(
          this.selectedItemId,
          statusType,
          newStatus
        )
      );

      this.toasterService.success(
        'Success',
        `${statusLabel} status updated to ${newStatus}`
      );

      // Reload the item to get updated data
      await this.loadItemForEdit(this.selectedItemId);
    } catch (error: any) {
      console.error('Error updating status:', error);
      const message =
        error?.error?.message || `Failed to update ${statusLabel} status`;
      this.toasterService.error('Error', message);
    } finally {
      this.isLoading = false;
    }
  }

  onCancel() {
    this.router.navigate(['/settings/masters/fiscal-period']);
  }

  getStatusClass(status: string): string {
    return `status-${status || 'open'}`;
  }

  getNextStatusOptions(currentStatus: string): any[] {
    switch (currentStatus) {
      case 'open':
        return [
          { label: 'Close', value: 'closed' },
          { label: 'Lock', value: 'locked' },
        ];
      case 'closed':
        return [
          { label: 'Reopen', value: 'open' },
          { label: 'Lock', value: 'locked' },
        ];
      default:
        return [];
    }
  }

  getSubsidiaryName(subsidiaryId: any): string {
    if (!subsidiaryId) return '-';
    const subsidiary = this.subsidiaryOptions.find(
      (s) => s.value == subsidiaryId
    );
    return subsidiary?.label || '-';
  }

  formatStatusDisplay(status: string): string {
    switch (status) {
      case 'open':
        return '✓ Open';
      case 'closed':
        return '○ Closed';
      case 'locked':
        return '🔒 Locked';
      default:
        return status || '-';
    }
  }

  private formatDate(date: Date | string | null): string | null {
    if (!date) return null;
    if (typeof date === 'string') return date;

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}
